/**
 * AdminAuditLogPage — customer-scoped admin audit log viewer.
 *
 * Phase 10 of the phone admin IVR console. Read-only table of every
 * action that landed in admin_audit_log for the entered customer.
 * Filters: action verb, actor kind. Pagination: 50/100/200/ALL,
 * default 50 (matches /folders/:id pattern).
 *
 * Columns: When | Actor | Action | Target | Details
 *   - Actor: 'phone +12125551234' for phone_admin, email for portal_user, '—' for system
 *   - Target: links to /ivr/trees/:id when target_kind='ivr_node' (resolves via parent tree),
 *     /folders/:id for folder targets, etc. Best-effort.
 *   - Details: expandable JSON dump (before/after/details merged).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminAuditLog, type AuditEntryRow, type AuditActorKind } from '@/api/auditLog';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { DateTimePicker } from '@/components/DateTimePicker';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * The audit log keeps `fromDate` / `toDate` as YYYY-MM-DD strings so
 * `addOneDay` and the backend's exclusive-to handling stay simple.
 * DateTimePicker wants YYYY-MM-DDTHH:MM, so we bridge at the picker
 * boundary: append a default time on read, slice it off on change.
 */
function dateOnlyToFull(d: string, defaultTime: string): string {
  if (!d) return '';
  return d.length > 10 ? d : `${d}T${defaultTime}`;
}
function fullToDateOnly(d: string): string {
  return d ? d.slice(0, 10) : '';
}

const PAGE_SIZES = [50, 100, 200, 'ALL'] as const;
type PageSize = typeof PAGE_SIZES[number];

const ACTOR_KIND_OPTIONS: { value: AuditActorKind | '__all__'; label: string }[] = [
  { value: '__all__', label: 'All actors' },
  { value: 'phone_admin', label: 'Phone admin' },
  { value: 'portal_user', label: 'Portal user' },
  { value: 'system', label: 'System' },
];

/** Known action verbs surfaced as autocomplete suggestions on the
 *  free-text action filter. Not exhaustive — the input still accepts any
 *  string the user types. Grouped by category for readability. */
const CONFERENCE_ACTIONS = [
  'conference.mute_participant',
  'conference.unmute_participant',
  'conference.mute_all_non_mod',
  'conference.unmute_all_non_mod',
  'conference.kick',
  'conference.lock',
  'conference.unlock',
  'conference.start_recording',
  'conference.stop_recording',
  'conference.dial_out',
  'conference.play',
  'conference.end',
  'conference.set_member_flag',
  'conference.set_volume',
] as const;

const ACTION_SUGGESTIONS: readonly string[] = [
  ...CONFERENCE_ACTIONS,
];

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatActor(row: AuditEntryRow): string {
  if (row.actor_kind === 'phone_admin') {
    return row.actor_e164 ? `Phone ${row.actor_e164}` : 'Phone admin';
  }
  if (row.actor_kind === 'portal_user') {
    return row.actor_email ?? 'Portal user';
  }
  return 'System';
}

/** Add a day to a YYYY-MM-DD string. Used to convert the user-facing
 *  inclusive "to" date into the backend's exclusive upper bound. */
function addOneDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
}

const UUID_REGEX = /"([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"/gi;

/**
 * Render JSON for the detail dump, replacing any UUID value the
 * backend resolved into a friendly ref ("REC-0042", "IVR-1042", etc.)
 * with just the ref string itself. So
 *
 *   "intro2_recording_id": "5bf592f7-bb09-48f7-a40f-fa49b4b38795"
 *
 * displays as
 *
 *   "intro2_recording_id": "REC-0042"
 *
 * UUIDs that don't resolve to any known entity (deleted rows, target
 * kinds without ref_number columns) keep their raw UUID. The raw
 * before_state / after_state JSON is still in the API response, so
 * any automation that wants the UUIDs can read them there.
 */
function renderJsonWithRefs(
  value: unknown,
  refs: Record<string, string>,
): string {
  const json = JSON.stringify(value, null, 2);
  if (!refs || Object.keys(refs).length === 0) return json;
  return json.replace(UUID_REGEX, (match, uuid: string) => {
    const ref = refs[uuid.toLowerCase()];
    return ref ? `"${ref}"` : match;
  });
}

export function AdminAuditLogPage() {
  const customerId = useEffectiveCustomerId();
  const [actionFilter, setActionFilter] = useState('');
  const [actorKind, setActorKind] = useState<AuditActorKind | ''>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const apiPageSize = pageSize === 'ALL' ? 500 : pageSize;

  // The backend treats `to` as EXCLUSIVE so the user's "to=2026-05-15"
  // (meaning "include everything through end of today") needs to be sent
  // as the start of the next day. We compute that here so the user picks
  // a date and gets the inclusive behavior they'd expect.
  const apiTo = toDate ? addOneDay(toDate) : undefined;

  const query = useQuery({
    queryKey: ['adminAuditLog', customerId, actionFilter, actorKind, fromDate, apiTo, page, apiPageSize],
    queryFn: () => adminAuditLog.list({
      customer_id: customerId!,
      ...(actionFilter ? { action: actionFilter } : {}),
      ...(actorKind ? { actor_kind: actorKind } : {}),
      ...(fromDate ? { from: fromDate } : {}),
      ...(apiTo ? { to: apiTo } : {}),
      page,
      page_size: apiPageSize,
    }),
    enabled: Boolean(customerId),
  });

  if (!customerId) {
    return (
      <>
        <PageHeader
          eyebrow="Settings"
          title="Admin audit log"
          description="Every change made by phone admins or portal users on this account."
        />
        <div className="text-sm text-muted-foreground">
          Open a customer from the Customers list first (super_admin /
          reseller_owner). The audit log is customer-scoped.
        </div>
      </>
    );
  }

  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const displayRefs = query.data?.display_refs ?? {};
  const pageCount = pageSize === 'ALL'
    ? 1
    : Math.max(1, Math.ceil(total / apiPageSize));
  const showingFrom = total === 0 ? 0 : page * apiPageSize + 1;
  const showingTo = Math.min((page + 1) * apiPageSize, total);

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Admin audit log"
        description="Every change made by phone admins or portal users on this account."
      />

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <Input
          placeholder="Filter by action (e.g. node.toggle_active)…"
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
          className="max-w-sm"
          list="audit-action-suggestions"
        />
        <datalist id="audit-action-suggestions">
          {ACTION_SUGGESTIONS.map((a) => (
            <option key={a} value={a} />
          ))}
        </datalist>
        <Select
          value={actorKind === '' ? '__all__' : actorKind}
          onValueChange={(v) => {
            setActorKind(v === '__all__' ? '' : v as AuditActorKind);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACTOR_KIND_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Date range. The backend treats `to` as exclusive; the page
            adds one day before sending so a user-facing "to today"
            includes everything from today. */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>From</span>
          <DateTimePicker
            value={dateOnlyToFull(fromDate, '00:00')}
            onChange={(v) => { setFromDate(fullToDateOnly(v)); setPage(0); }}
            defaultTimeOnDateSelect="00:00"
            placeholder="Start"
          />
          <span>To</span>
          <DateTimePicker
            value={dateOnlyToFull(toDate, '23:59')}
            onChange={(v) => { setToDate(fullToDateOnly(v)); setPage(0); }}
            defaultTimeOnDateSelect="23:59"
            placeholder="End"
          />
          {(fromDate || toDate) && (
            <button
              type="button"
              onClick={() => { setFromDate(''); setToDate(''); setPage(0); }}
              className="text-xs text-primary hover:underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {query.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : query.isError ? (
        <div role="alert" className="text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : 'Failed to load audit log'}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No audit entries match these filters.
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="grid grid-cols-[10rem_12rem_12rem_1fr_4rem] gap-0 border-b border-border bg-slate-50/60 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              <div>When</div>
              <div>Actor</div>
              <div>Action</div>
              <div>Target</div>
              <div />
            </div>
            <ul className="divide-y divide-border">
              {rows.map((r) => (
                <li key={r.id}>
                  <div className="grid grid-cols-[10rem_12rem_12rem_1fr_4rem] gap-0 items-center px-4 py-2 text-xs">
                    <div className="text-muted-foreground">{formatDateTime(r.created_at)}</div>
                    <div className="truncate" title={formatActor(r)}>{formatActor(r)}</div>
                    <div className="font-mono truncate" title={r.action}>{r.action}</div>
                    <div className="truncate text-muted-foreground" title={
                      r.target_kind && r.target_id
                        ? `${r.target_kind} ${r.target_id}`  // raw UUID still in the tooltip for debugging
                        : (r.target_kind ?? '')
                    }>
                      {r.target_kind
                        ? `${r.target_kind}: ${r.target_ref ?? r.target_id ?? '—'}`
                        : '—'}
                    </div>
                    <div className="flex justify-end">
                      <Button
                        size="sm" variant="ghost"
                        className="h-6 px-2 text-[10px]"
                        onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                      >
                        {expandedId === r.id ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                  </div>
                  {expandedId === r.id && (
                    <div className="px-4 pb-3 text-[11px] bg-slate-50/40">
                      <pre className="whitespace-pre-wrap font-mono text-[11px] text-slate-700 max-w-full overflow-auto">
{renderJsonWithRefs({
  before_state: r.before_state,
  after_state: r.after_state,
  details: r.details,
}, displayRefs)}
                      </pre>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Pagination footer — same shape as folder-detail / auth-list. */}
      {rows.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap text-xs pt-3">
          <div className="text-muted-foreground">
            Showing {showingFrom}–{showingTo} of {total}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const v = e.target.value;
                setPageSize(v === 'ALL' ? 'ALL' : (Number(v) as PageSize));
                setPage(0);
              }}
              className="rounded border border-input bg-background px-2 py-1 text-xs"
            >
              {PAGE_SIZES.map((n) => (
                <option key={String(n)} value={String(n)}>{n}</option>
              ))}
            </select>
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0 || pageSize === 'ALL'}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="font-mono">
              Page {page + 1} of {pageCount}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page + 1 >= pageCount || pageSize === 'ALL'}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Next page"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
